import React, { useEffect, useState } from "react";
import { api, formatIDR, formatDate, formatDateTime, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DismissibleMemo } from "@/components/DismissibleMemo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Package, ShoppingCart, XCircle, CheckCircle2, Clock } from "lucide-react";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";

const STATUS_META = {
  pending: { label: "Menunggu Supplier", color: "bg-amber-100 text-amber-700 hover:bg-amber-100", icon: Clock },
  approved: { label: "Disetujui", color: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100", icon: CheckCircle2 },
  rejected: { label: "Ditolak", color: "bg-red-100 text-red-700 hover:bg-red-100", icon: XCircle },
  cancelled: { label: "Dibatalkan", color: "bg-slate-200 text-slate-700 hover:bg-slate-200", icon: XCircle },
};

export default function MitraOrders() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ items: [{ product_id: "", qty: 1, notes: "" }], notes: "", needed_by: "" });
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/mitra/orders"), api.get("/products")]);
    setOrders(a.data); setProducts(b.data);
  };
  useEffect(() => { load(); }, []);

  const tf = useTableFilter(orders, {
    searchKeys: ["notes", "status"],
    defaultSort: { key: "created_at", dir: "desc" },
  });

  const addLine = () => setForm((f) => ({ ...f, items: [...f.items, { product_id: "", qty: 1, notes: "" }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateLine = (i, k, v) => setForm((f) => {
    const items = [...f.items]; items[i] = { ...items[i], [k]: v }; return { ...f, items };
  });

  const submit = async () => {
    const items = form.items.filter((l) => l.product_id && Number(l.qty) > 0)
      .map((l) => ({ product_id: l.product_id, qty: Number(l.qty), notes: l.notes || "" }));
    if (items.length === 0) return toast.error("Pilih minimal 1 produk dengan qty > 0");
    try {
      await api.post("/mitra/orders", { items, notes: form.notes, needed_by: form.needed_by || null });
      toast.success("Order terkirim ke Supplier — status: menunggu keputusan");
      setOpen(false);
      setForm({ items: [{ product_id: "", qty: 1, notes: "" }], notes: "", needed_by: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cancel = async (o) => {
    if (!window.confirm(`Batalkan order dengan ${o.items.length} item?`)) return;
    try {
      await api.post(`/mitra/orders/${o.id}/cancel`, {});
      toast.success("Order dibatalkan");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const pendingCount = orders.filter((o) => o.status === "pending").length;

  return (
    <>
      <PageHeader
        title="Pemesanan Stok ke Supplier"
        subtitle="Ajukan permintaan restock — Supplier akan menyetujui/menolak sesuai stok & SDM"
        testId="page-mitra-orders"
        actions={
          <Button onClick={() => setOpen(true)} data-testid="btn-new-order" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
            <Plus className="w-4 h-4 mr-2"/> Buat Order
          </Button>
        }
      />

      <DismissibleMemo
        id="mitra-order-workflow"
        className="p-4 pr-12 border-l-4 border-l-blue-500 bg-blue-50/40 mb-4"
        testId="order-info-card"
      >
        <div className="flex items-start gap-3">
          <ShoppingCart className="w-5 h-5 text-blue-700 shrink-0 mt-0.5"/>
          <div className="text-sm text-slate-700 flex-1">
            <div className="font-bold text-blue-800 mb-1">Alur Pemesanan</div>
            <ol className="list-decimal ml-5 text-xs space-y-0.5">
              <li>Buat order dengan produk & jumlah yang Anda butuhkan.</li>
              <li>Supplier akan review — bisa <b>Approve</b> (setuju kirim, jumlah dikirim mungkin berbeda sesuai stok) atau <b>Reject</b>.</li>
              <li>Setelah approved, Supplier akan mengatur penyaluran ke toko Anda — Anda akan lihat di riwayat "Penyaluran Retail".</li>
              <li>Order <b>tidak</b> otomatis menambah stok toko — hanya sebagai permintaan / bukti pesanan.</li>
            </ol>
          </div>
        </div>
      </DismissibleMemo>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <Card className="p-4 border-l-4 border-l-amber-400 rounded-md">
          <div className="overline mb-1">Menunggu Supplier</div>
          <div className="font-mono-num text-2xl font-bold text-amber-600">{orders.filter((o) => o.status === "pending").length}</div>
        </Card>
        <Card className="p-4 border-l-4 border-l-emerald-400 rounded-md">
          <div className="overline mb-1">Disetujui</div>
          <div className="font-mono-num text-2xl font-bold text-emerald-600">{orders.filter((o) => o.status === "approved").length}</div>
        </Card>
        <Card className="p-4 border-l-4 border-l-red-400 rounded-md">
          <div className="overline mb-1">Ditolak / Dibatalkan</div>
          <div className="font-mono-num text-2xl font-bold text-red-600">{orders.filter((o) => o.status === "rejected" || o.status === "cancelled").length}</div>
        </Card>
      </div>

      <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
        <div className="p-3 border-b"><TableToolbar tf={tf} placeholder="Cari catatan / status..."/></div>
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead className="w-10">#</TableHead>
            <SortHeader tf={tf} col="created_at">Tanggal Order</SortHeader>
            <TableHead>Item</TableHead>
            <SortHeader tf={tf} col="needed_by">Target Kirim</SortHeader>
            <SortHeader tf={tf} col="status">Status</SortHeader>
            <TableHead>Respon Supplier</TableHead>
            <TableHead className="w-24 text-right">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {tf.rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Belum ada order.</TableCell></TableRow>}
            {tf.rows.map((o, idx) => {
              const meta = STATUS_META[o.status] || STATUS_META.pending;
              const Icon = meta.icon;
              return (
                <TableRow key={o.id} className="table-row-hover cursor-pointer" onClick={() => setDetail(o)}>
                  <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                  <TableCell className="font-mono-num text-xs">{formatDateTime(o.created_at)}</TableCell>
                  <TableCell className="text-xs text-slate-600 max-w-md truncate">
                    {(o.items || []).map((it) => `${it.product_name}×${it.qty}`).join(", ")}
                  </TableCell>
                  <TableCell className="text-xs">{o.needed_by ? formatDate(o.needed_by) : "-"}</TableCell>
                  <TableCell>
                    <Badge className={meta.color}><Icon className="w-3 h-3 mr-1"/>{meta.label}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600 max-w-xs truncate">{o.owner_note || "-"}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {o.status === "pending" && (
                      <Button size="sm" variant="ghost" onClick={() => cancel(o)} className="h-7 text-xs text-red-500 hover:text-red-600" data-testid={`btn-cancel-${o.id}`}>
                        Batal
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Dialog: Order Baru */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Buat Order ke Supplier</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Target Tanggal Kirim (opsional)</Label>
                <Input type="date" value={form.needed_by} onChange={(e) => setForm({ ...form, needed_by: e.target.value })} data-testid="input-needed-by"/>
              </div>
              <div>
                <Label>Catatan (opsional)</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="mis. Mendesak untuk stok akhir minggu" data-testid="input-order-note"/>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label>Item yang Dipesan</Label>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="btn-add-order-line"><Plus className="w-3 h-3 mr-1"/>Tambah Baris</Button>
            </div>

            <div className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead>Produk</TableHead>
                  <TableHead className="w-24">Qty</TableHead>
                  <TableHead>Catatan Item</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {form.items.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Select value={l.product_id} onValueChange={(v) => updateLine(i, "product_id", v)}>
                          <SelectTrigger data-testid={`order-line-product-${i}`}><SelectValue placeholder="Pilih produk"/></SelectTrigger>
                          <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input type="number" min={1} value={l.qty} onChange={(e) => updateLine(i, "qty", e.target.value)} data-testid={`order-line-qty-${i}`}/>
                      </TableCell>
                      <TableCell>
                        <Input value={l.notes} onChange={(e) => updateLine(i, "notes", e.target.value)} placeholder="opsional"/>
                      </TableCell>
                      <TableCell><Button size="icon" variant="ghost" onClick={() => removeLine(i)}><Trash2 className="w-3 h-3 text-red-500"/></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submit} data-testid="btn-submit-order" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
              <ShoppingCart className="w-4 h-4 mr-2"/> Kirim Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Detail Order */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Detail Order</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3">
              <div className="text-sm space-y-1">
                <div><b>Tanggal:</b> {formatDateTime(detail.created_at)}</div>
                {detail.needed_by && <div><b>Target Kirim:</b> {formatDate(detail.needed_by)}</div>}
                <div><b>Status:</b> <Badge className={(STATUS_META[detail.status] || STATUS_META.pending).color}>{(STATUS_META[detail.status] || STATUS_META.pending).label}</Badge></div>
                {detail.notes && <div><b>Catatan Anda:</b> {detail.notes}</div>}
                {detail.owner_note && <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs"><b>Respon Supplier:</b> {detail.owner_note}</div>}
                {detail.close_memo && (
                  <div className="p-2 bg-slate-50 border border-slate-200 rounded text-xs whitespace-pre-line">
                    <b>Memo Penutupan:</b> {detail.close_memo}
                  </div>
                )}
              </div>
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead>Produk</TableHead>
                  <TableHead className="text-right">Diminta</TableHead>
                  {detail.status === "approved" && <TableHead className="text-right">Disetujui</TableHead>}
                  <TableHead>Catatan</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(detail.items || []).map((it, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{it.product_name}</TableCell>
                      <TableCell className="text-right font-mono-num">{it.qty}</TableCell>
                      {detail.status === "approved" && (
                        <TableCell className="text-right font-mono-num font-semibold">
                          {detail.approved_items?.find((approved) => approved.product_id === it.product_id)
                            ?.approved_qty ?? it.qty}
                        </TableCell>
                      )}
                      <TableCell className="text-xs text-slate-500">{it.notes || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
